import { useMemo, useState, useCallback, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Navigate } from 'react-router-dom';
import { LoadingScreen } from '@/components/LoadingScreen';
import { InAppBrowserOverlay } from '@/components/InAppBrowserOverlay';
import { detectInAppBrowser } from '@/lib/in-app-browser';

// Session storage key for in-app browser overlay dismissal
const IN_APP_BROWSER_DISMISSED_KEY = 'inAppBrowserOverlayDismissed';

/**
 * Check if the in-app browser overlay has been dismissed this session.
 * Used by other contexts to know when to show their modals.
 */
export function isInAppBrowserOverlayDismissed(): boolean {
  if (typeof window === 'undefined') return true;
  return sessionStorage.getItem(IN_APP_BROWSER_DISMISSED_KEY) === 'true';
}

/**
 * Check if we're in an in-app browser and the overlay is still showing (not dismissed).
 * Returns true if user is blocked by the overlay, false if they can proceed.
 */
export function isBlockedByInAppBrowserOverlay(): boolean {
  if (typeof window === 'undefined') return false;
  const browserInfo = detectInAppBrowser();
  return browserInfo.isInAppBrowser && !isInAppBrowserOverlayDismissed();
}

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, loading } = useAuth();
  const [isDismissed, setIsDismissed] = useState(() => isInAppBrowserOverlayDismissed());

  // Memoize browser detection to avoid recalculating on every render
  const browserInfo = useMemo(() => detectInAppBrowser(), []);

  // Handle dismissal
  const handleDismiss = useCallback(() => {
    sessionStorage.setItem(IN_APP_BROWSER_DISMISSED_KEY, 'true');
    setIsDismissed(true);
    // Dispatch a custom event so other contexts can react
    window.dispatchEvent(new CustomEvent('inAppBrowserOverlayDismissed'));
  }, []);

  // Reset dismissal state when user logs out (on component mount, check if user exists)
  useEffect(() => {
    if (!user) {
      sessionStorage.removeItem(IN_APP_BROWSER_DISMISSED_KEY);
      setIsDismissed(false);
    }
  }, [user]);

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Show overlay for in-app browsers if not dismissed
  if (browserInfo.isInAppBrowser && !isDismissed) {
    return (
      <InAppBrowserOverlay
        browserName={browserInfo.browserName}
        platform={browserInfo.platform}
        onDismiss={handleDismiss}
      />
    );
  }

  return <>{children}</>;
};
